
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import BillingOptions from "./BillingOptions";
import SubscriptionInfoCard from "./SubscriptionInfoCard";
import { supabase } from "@/integrations/supabase/client";
import { isIOSDevice } from "@/utils/deviceDetection";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { InfoIcon, User } from "lucide-react";
import { Link } from "react-router-dom";

interface PremiumCheckoutProps {
  strategy: any;
  advantages?: string[];
}

const PremiumCheckout = ({ strategy }: PremiumCheckoutProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [billingOption, setBillingOption] = useState<"monthly" | "yearly">("yearly");
  const isIOS = isIOSDevice();

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        // Now we don't store the strategy follow here
        // We'll do it after payment is successful
        
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        navigate('/payment', {
          state: {
            strategy,
            isYearly: billingOption === "yearly"
          }
        });
      } else {
        toast({
          title: "Authentication required",
          description: "Please log in to follow premium strategies",
          variant: "destructive"
        });
        navigate("/auth");
        return;
      }
    } catch (error) {
      console.error("Error in handleConfirm:", error);
      toast({
        title: "Error",
        description: "Failed to follow strategy. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-semibold mb-6 text-[#243949]">Advantages of METRADE Premium Subscription</h2>
      
      {isIOS && (
        <Alert className="mb-6 bg-blue-50 border-blue-200">
          <InfoIcon className="h-4 w-4 text-blue-500" />
          <AlertDescription className="text-sm text-gray-700">
            <strong>iOS App Store Notice:</strong> Due to App Store policies, purchases are not available in the iOS app. 
            Please manage your subscription through your account settings.
          </AlertDescription>
        </Alert>
      )}
      
      <SubscriptionInfoCard />
      
      {!isIOS && (
        <>
          <BillingOptions 
            billingOption={billingOption}
            setBillingOption={setBillingOption}
          />
          
          <Button 
            onClick={handleConfirm} 
            disabled={isSubmitting} 
            className="w-full bg-[#517FA4] hover:bg-[#243949] text-white py-6 text-lg font-medium shadow-lg hover:shadow-xl transition-all"
          >
            {isSubmitting ? "Processing..." : "Continue to Payment"}
          </Button>
        </>
      )}
      
      {isIOS && (
        <div className="text-center mt-8">
          <Link to="/account">
            <Button 
              className="bg-[#517FA4] hover:bg-[#243949] text-white py-6 text-lg font-medium shadow-lg hover:shadow-xl transition-all px-8"
            >
              Manage Account <User className="ml-2 h-5 w-5" />
            </Button>
          </Link>
          <p className="mt-4 text-sm text-gray-500">
            To subscribe, please visit our website at <a href="https://metrade.io" className="text-blue-600 underline" target="_blank" rel="noopener noreferrer">metrade.io</a>
          </p>
        </div>
      )}
    </div>
  );
};

export default PremiumCheckout;
